import React from "react";
import {useQuery} from "@tanstack/react-query";
import {Link} from "react-router-dom";
import {fetchBillingTotals} from "@/lib/api";
import {useStats} from "@/hooks/useStats";
import {useDashboardOrg} from "@/app/DashboardOrgContext";
import {ElectricityStack, DollarSign, Users, ArrowRightCircle} from "@/components/icons";
import {Card, CardHeader, CardTitle, CardContent} from "@/components/ui/card";

export default function ConsumptionsStat() {
    // Manager → org-scoped billing totals (/api/org/<id>/billing/totals); admin →
    // fleet. Key includes org + period so switching org never shows the prior org's
    // totals; scopeReady gates the manager pre-selection window.
    const {dashboardOrgId, scopeReady} = useDashboardOrg();
    const {data, isLoading, isError, dataUpdatedAt} = useQuery({
        queryKey: ["billing-totals", dashboardOrgId, "month"],
        queryFn: () => fetchBillingTotals('month', dashboardOrgId),
        enabled: scopeReady,
        refetchInterval: 60_000,
    });
    const stats = useStats();

    if (data?.no_units_assigned) return null;

    const activeDevices = stats.activeDevices;


    return (
        <Card>
            <CardContent className="station-overview-wrap py-4">
                <h6 className="text-base font-semibold">Sessions Overview <span className="text-xs font-normal text-slate-400"> - Last 30d</span></h6>
                <div className="station-overview-container">
                    <StatCard
                        label="Energy"
                        value={data?.total_energy_kwh}
                        unit="KWh"
                        color={"oklch(68.5% 0.169 237.323)"}
                        AccentIcon={ElectricityStack}
                        to="sessions"
                        activeDevices={activeDevices}
                        isLoading={isLoading}
                        isError={isError}
                    />
                    <StatCard
                        label="Revenue"
                        value={data?.total_revenue}
                        unit="USD"
                        AccentIcon={DollarSign}
                        to="billing"
                        activeDevices={activeDevices}
                        isLoading={isLoading}
                        isError={isError}
                    />
                    <StatCard
                        label="Sessions"
                        value={data?.total_sessions}
                        color={"oklch(70.4% 0.14 182.503)"}
                        AccentIcon={Users}
                        to="sessions"
                        activeDevices={activeDevices}
                        isLoading={isLoading}
                        isError={isError}
                    />
                </div>
            </CardContent>
        </Card>
    );
}

function StatCard({
    label,
    value,
    unit,
    iconStrokeWidth=1.5,
    iconColor="var(--color-green)",
    color,
    AccentIcon,
    to,
    activeDevices,
    isLoading,
    isError,
}: {
    label: string;
    value?: number;
    unit?: string;
    iconStrokeWidth?: number,
    iconColor?: string,
    color?: string,
    AccentIcon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number; color?: string }>;
    to?: string;
    activeDevices: number;
    isLoading: boolean;
    isError: boolean;
}) {
    const formatted = value != null
        ? value.toLocaleString(undefined, {maximumFractionDigits: 2})
        : "0";

    const arrow = (
        <div className="card-arrow-wrap">
            <div className="card-arrow-container">
                <span className="in-active-icon opacity-[.95]"><AccentIcon strokeWidth={iconStrokeWidth} color={"oklch(52% 0.105 223.128)"} size={38}/></span>
                <span className="active-icon opacity-[.95]" role="presentation"><ArrowRightCircle strokeWidth={iconStrokeWidth} color={"oklch(52% 0.105 223.128)"} size={38}/></span>
            </div>
        </div>
    );

    return (
        <div className="each-card-container" data-testid={`stat-${label.toLowerCase()}`}>
            <h3 className={"text-xs"}>{label}</h3>
            {isLoading ? (
                <h1><span className="animate-pulse">...</span></h1>
            ) : isError ? (
                <h1><span style={{color: "red", fontSize: 14}}>Failed to load</span></h1>
            ) : (
                <h1 style={{color:color}}>{formatted} {unit && <span>({unit})</span>}</h1>
            )}
            {to ? <Link to={to}>{arrow}</Link> : arrow}
        </div>
    );
}
